import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, Printer } from "lucide-react";
import { fmtQty, type StockUnitRecord } from "@/types/inventory";
import { Barcode } from "@/components/inventory/Barcode";

/** Roll labels to print and stick on the rolls: product, shade, lot, length and a barcode that finds the roll when scanned */
export function LabelsPage() {
  const [params] = useSearchParams();
  const { error } = useToast();
  const ids = params.get("ids") || "";
  const [units, setUnits] = useState<StockUnitRecord[] | null>(null);

  useEffect(() => {
    if (!ids) return setUnits([]);
    api
      .get(`/api/Inventory/Labels?ids=${ids}`)
      .then((r) => setUnits(unwrapArray<StockUnitRecord>(r)))
      .catch((e: any) => {
        error("Fetch Error", e.message || "Failed to load labels");
        setUnits([]);
      });
  }, [ids]);

  return (
    <div className="space-y-4">
      <style>{`@media print { body * { visibility: hidden; } .label-sheet, .label-sheet * { visibility: visible; } .label-sheet { position: absolute; left: 0; top: 0; width: 100%; } .no-print { display: none !important; } @page { margin: 8mm; } }`}</style>
      <div className="no-print flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Link to="/inventory" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-1">
            <ArrowLeft className="size-3" /> Inventory
          </Link>
          <h1 className="text-2xl font-bold tracking-tight">Roll labels</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{units ? `${units.length} label${units.length === 1 ? "" : "s"}` : ""} · scanning a label in Inventory opens the roll</p>
        </div>
        <Button onClick={() => window.print()} disabled={!units?.length} className="gap-1.5">
          <Printer className="size-4" /> Print
        </Button>
      </div>
      {!units ? (
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      ) : (
        <div className="label-sheet grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {units.map((u) => (
            <div key={u.id} className="border border-black/60 rounded-md bg-white text-black p-3 break-inside-avoid" style={{ breakInside: "avoid" }}>
              <div className="text-sm font-bold leading-tight">{u.product?.name}</div>
              <div className="text-xs">{u.variant?.name || "No shade"} · Lot {u.lot?.lot_number}</div>
              <div className="flex justify-between text-xs mt-1">
                <span>Roll <b>{u.unit_no}</b></span>
                <span><b>{fmtQty(u.initial_quantity, u.unit)}</b></span>
              </div>
              {(u.net_weight != null || u.gross_weight != null) && <div className="text-[11px]">{u.net_weight != null ? `Net ${Number(u.net_weight)} kg` : ""}{u.gross_weight != null ? ` · Gross ${Number(u.gross_weight)} kg` : ""}</div>}
              <div className="mt-2">{u.barcode ? <Barcode value={u.barcode} /> : <span className="text-xs">No code</span>}</div>
            </div>
          ))}
          {!units.length && <p className="text-sm text-muted-foreground">No rolls chosen. Tick rolls in Inventory and press Print labels.</p>}
        </div>
      )}
    </div>
  );
}
